import type { ElementType, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  ResponsiveValue,
  WidthProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  maxWidth,
  minHeight,
  minWidth,
  position,
  size,
  themeGet,
  width,
} from 'styled-system';

import { MessageType } from '../../enums/Alert';
import { useLocalization } from '../../providers/LocaleProvider';
import { focusOutlineCSS } from '../../theme/styles';
import { boxSizing, cursor, fill, outlineColor } from '../../theme/system-utilities';
import type { FlexProps } from '../Flex';
import { Flex } from '../Flex';
import Icon from '../Icon';
import { BodyRegularBR2 } from '../Typography';

export interface PlainAlertProps {
  type: MessageType;
  message: string;
}

export interface AlertProps
  extends BorderProps, BorderRadiusProps, FlexProps, Pick<PlainAlertProps, 'type'> {
  withIcon?: boolean;
  children?: ReactNode;
  dataTestId?: string;
  className?: string;
  html?: string;
  onClick?: () => void;
  withBorder?: boolean;
  role?: string;
  as?: ElementType;
  ref?: React.Ref<HTMLDivElement>;
}

interface IconStyleProps {
  fill?: string;
  minWidth?: ResponsiveValue<string | number>;
  minHeight?: ResponsiveValue<string | number>;
  size?: ResponsiveValue<string | number>;
}

type AlertIcon = 'error' | 'success' | 'warning' | 'info';

const ICON_SIZES = [24, null, null, 32];

const Container = styled(Flex)<BorderColorProps>`
  ${position};
  ${backgroundColor};
  ${border};
  ${borderColor};
  ${borderRadius};
  ${maxWidth};
  ${cursor};
  ${boxSizing};
  ${outlineColor};

  scroll-margin-top: 16px;
`;

const containerStyles = {
  position: 'relative',
  alignItems: 'center',
  border: '1px solid',
  borderRadius: 'medium',
  py: 'sp_8',
  pl: 'sp_8',
  pr: 'sp_16',
  mb: 'sp_8',
  boxSizing: 'border-box',
  outlineColor: 'sys.secondary.border.focus',
} as const;

const StyledIcon = styled(Icon)<IconStyleProps>`
  ${size};
  ${minWidth};
  ${minHeight};
  ${fill};
`;

const styledIconStyles = {
  size: ICON_SIZES,
  minWidth: ICON_SIZES,
  minHeight: ICON_SIZES,
};

const AlertRichText = styled(BodyRegularBR2)<{ state?: string }>`
  white-space: pre-line;
  ul,
  ol {
    margin: 0;
    padding-left: ${themeGet('space.sp_16')}px;

    > li {
      padding-left: 0;
    }
  }

  a {
    text-decoration-color: inherit;
    border-radius: ${themeGet('radii.medium')};
    color: ${props => themeGet(`colors.${props.color}`)};

    ${focusOutlineCSS};

    &:hover,
    &:active {
      color: ${props => themeGet(`colors.sys.${props.state}.text.hover`)};
    }
  }
`;

const Content = styled(BodyRegularBR2)<WidthProps>`
  ${width}
`;

export const getAlertBackgroundColor = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'sys.success.background.weak';
    case MessageType.Error:
      return 'sys.error.background.weak';
    case MessageType.Warning:
      return 'sys.warning.background.weak';
    default:
      return 'sys.secondary.background.weak';
  }
};

export const getAlertTextColor = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'sys.success.text.strong';
    case MessageType.Error:
      return 'sys.error.text.strong';
    case MessageType.Warning:
      return 'sys.warning.text.strong';
    default:
      return 'sys.secondary.text.strong';
  }
};

export const getAlertIconColor = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'sys.success.icon.strong';
    case MessageType.Error:
      return 'sys.error.icon.strong';
    case MessageType.Warning:
      return 'sys.warning.icon.strong';
    default:
      return 'sys.secondary.icon.strong';
  }
};

export const getIconAltText = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'h24_success_icon';
    case MessageType.Error:
      return 'h24_error_icon';
    case MessageType.Warning:
      return 'h24_warning_icon';
    default:
      return 'h24_info_icon';
  }
};

export const getAlertBorderColor = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'sys.success.border.default';
    case MessageType.Error:
      return 'sys.error.border.default';
    case MessageType.Warning:
      return 'sys.warning.border.default';
    default:
      return 'sys.secondary.border.default';
  }
};

const getColorState = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
      return 'success';
    case MessageType.Error:
      return 'error';
    case MessageType.Warning:
      return 'warning';
    default:
      return 'secondary';
  }
};

const getIconName = (type: MessageType): AlertIcon => {
  switch (type) {
    case MessageType.Success:
      return 'success';
    case MessageType.Error:
      return 'error';
    case MessageType.Warning:
      return 'warning';
    default:
      return 'info';
  }
};

const getDefaultRole = (type: MessageType) => {
  switch (type) {
    case MessageType.Success:
    case MessageType.Error:
      return 'alert';
    default:
      return null;
  }
};

const Alert = ({
  as,
  type,
  withIcon = true,
  role = getDefaultRole(type),
  dataTestId = 'alert',
  children,
  className,
  html,
  onClick,
  withBorder = true,
  ref,
  ...styleProps
}: AlertProps) => {
  const { t } = useLocalization();

  const containerBackgroundColor = getAlertBackgroundColor(type);
  const containerBorderColor = getAlertBorderColor(type);
  const textColor = getAlertTextColor(type);
  const iconColor = getAlertIconColor(type);
  const iconName = getIconName(type);
  const iconAltText = t(getIconAltText(type));
  const styledTextProps = {
    state: 'default',
    color: textColor,
    ml: withIcon ? 'sp_8' : 'sp_0',
  } as const;

  return (
    <Container
      as={as}
      className={className}
      {...(role && { role })}
      backgroundColor={containerBackgroundColor}
      borderColor={containerBorderColor}
      data-testid={dataTestId}
      ref={ref}
      onClick={onClick}
      {...containerStyles}
      {...(!!onClick
        ? {
            cursor: 'pointer',
          }
        : {})}
      {...(!withBorder
        ? {
            border: 'none',
          }
        : {})}
      {...styleProps}
    >
      {withIcon && (
        <StyledIcon
          name={iconName}
          fill={iconColor}
          ariaLabel={iconAltText}
          {...styledIconStyles}
        />
      )}
      {html ? (
        <AlertRichText
          as="span"
          {...styledTextProps}
          state={getColorState(type)}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Content width={1} as="span" {...styledTextProps}>
          {children}
        </Content>
      )}
    </Container>
  );
};

Alert.displayName = 'Alert';

export default Alert;
